import { ReactNode, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as KeyboardArrowRightIcon } from '../../../assets/icons/arrow-right-s-line.svg';
import { ReactComponent as CloseIcon } from '../../../assets/icons/close-line.svg';
import { ReactComponent as ErrorIcon } from '../../../assets/svg/error.svg';
import { ReactComponent as SuccessIcon } from '../../../assets/svg/success.svg';
import { ReactComponent as WarningIcon } from '../../../assets/svg/warning.svg';
import { useLocale } from '../../../context/NbLocalizationContext';
import { NbButton } from '../../../shared/components/button/Button';
import { NbIconButton } from '../../../shared/components/icon-button/IconButton';
import { NbLoader } from '../../../shared/components/loader/Loader';
import { routes } from '../../../shared/data/routes';
import { useEStream } from '../../../shared/services/sse/useEStream';
import { ClientSubscriptionWithMarketDataInfo, usePartnerSubscriptions } from '../services/usePartnerSubscriptions';
import classes from './SubscriptionExchangePopoverContent.module.scss';

export interface SubscriptionExchangePopoverContentProps {
  onHandleExchangeClose: () => void;
}

export const SubscriptionExchangePopoverContent = ({
  onHandleExchangeClose,
}: SubscriptionExchangePopoverContentProps) => {
  const { translation, translateWithParams } = useLocale();
  const { hasEStreamConnection } = useEStream();
  const [isLoading, setIsLoading] = useState(false);
  const [subscriptions, setSubscriptions] = useState<Array<ClientSubscriptionWithMarketDataInfo>>();
  const { getMarketIcon, getPartnerSubscriptionsWithMarketDataInfo } = usePartnerSubscriptions();
  const navigate = useNavigate();

  const getConnectionStateIcon = (realTime: boolean, _marketCode: string): ReactNode => {
    if (!hasEStreamConnection) {
      return <ErrorIcon />;
    }
    return realTime ? <SuccessIcon /> : <WarningIcon />;
    // TODO - https://atlassian.ebh.erste.hu/Confl/pages/viewpage.action?pageId=146447850 - Estream will tell us if a market is down and we need to display the icon according to it
  };

  const loadMarketStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      setSubscriptions(await getPartnerSubscriptionsWithMarketDataInfo());
    } catch (err) {
      setSubscriptions(undefined);
    } finally {
      setIsLoading(false);
    }
  }, [getPartnerSubscriptionsWithMarketDataInfo]);

  useEffect(() => {
    loadMarketStatus();
  }, [loadMarketStatus]);

  const handleArrowClick = () => {
    navigate(routes.dataServiceManagement);
    onHandleExchangeClose();
  };

  return (
    <div className={classes.exchange}>
      <div className={classes.exchangeHeader}>
        <NbButton
          onClick={handleArrowClick}
          variant="text"
          endIcon={<KeyboardArrowRightIcon />}
        >
          {translation.header.exchange.title}
        </NbButton>
        <NbIconButton
          icon={<CloseIcon />}
          onClick={onHandleExchangeClose}
        />
      </div>
      {isLoading && (
        <NbLoader
          horizontalCenter
          verticalCenter
        />
      )}
      {!isLoading && (
        <div className={classes.exchangeContent}>
          {!subscriptions && translation.header.exchange.error}

          {subscriptions &&
            subscriptions.map(subscriptionItem => (
              <div
                key={subscriptionItem.subscription.marketDataProvider}
                className={classes.exchangeItem}
              >
                <div className={classes.exchangeTitle}>
                  <div
                    className={classes.exchangeFlag}
                    title={subscriptionItem.marketDataProvider?.name || ''}
                  >
                    {getMarketIcon(subscriptionItem.marketDataProvider)}
                  </div>
                  <p>
                    {subscriptionItem.subscription.realtime
                      ? translation.header.exchange.realTime
                      : translateWithParams(translation.header.exchange.delay, { min: '15' })}
                  </p>
                </div>
                {getConnectionStateIcon(
                  subscriptionItem.subscription.realtime,
                  subscriptionItem.subscription.marketDataProvider,
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  );
};
